import { createContext } from 'react'

export type AiJobStatus = 'idle' | 'running' | 'ready' | 'aborted'

/** Source surface that owns the current job. Used to route auto-handoff. */
export type AiJobSource = 'AI_GENERATOR' | 'REPORT_GENERATOR'

export interface AiJobSnapshot {
  status: AiJobStatus
  source: AiJobSource | null
  /** Optional label shown beside the spinner. */
  label: string | null
  /** Workspace ID that owns this job, if scoped to a workspace. */
  workspaceId?: string | null
}

export interface BeginJobArgs<T> {
  source: AiJobSource
  /** Optional spinner label (e.g. "Generating timeline…"). */
  label?: string
  /** Runs the job. Receives an AbortSignal that fires on user abort. */
  run: (signal: AbortSignal) => Promise<T>
  /** Workspace ID that initiated the job. */
  workspaceId?: string
}

export interface BeginJobResult<T> {
  /** Resolves with the result, or null if aborted. */
  outcome: Promise<T | null>
}

export interface AiJobContextValue {
  snapshot: AiJobSnapshot
  /**
   * Start a job. Only one job runs at a time — call rejects if a job is
   * already in flight.
   */
  beginJob: <T>(args: BeginJobArgs<T>) => BeginJobResult<T>
  /** Trigger the abort signal of the in-flight job. No-op otherwise. */
  abort: () => void
  /**
   * Claim the stashed result for `source` (after `status === 'ready'`).
   * Returns the result and resets the context to idle. Returns null if
   * nothing is staged for the source, or if workspaceId does not match.
   */
  claim: <T = unknown>(source: AiJobSource, workspaceId?: string) => T | null
  /** Reset context to idle without claiming. */
  clear: () => void
  /**
   * Register a live result handler for a source. When the running job for
   * that source finishes, this handler fires immediately and the context
   * resets to idle — implementing the "stayed on the page, auto-open
   * review" behavior. Returns an unregister function; on unregister with
   * a finished-but-unclaimed result, the context transitions to `ready`
   * and the result is stashed for later `claim()`.
   */
  subscribeAutoHandoff: <T>(source: AiJobSource, handler: (result: T) => void) => () => void
}

export const AiJobContext = createContext<AiJobContextValue | undefined>(undefined)
